import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Loader2, CheckCircle, KeyRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import { FormPageShell } from "../components/FormPageShell";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const ForgotPasswordPage = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");

  const [step, setStep] = useState(resetToken ? "reset" : "request");
  const [method, setMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (method === "email" && !email) {
      toast.error(t("enterEmail"));
      return;
    }
    if (method === "phone" && !phone) {
      toast.error(t("enterPhone"));
      return;
    }

    setLoading(true);
    try {
      const payload = method === "email" 
        ? { email } 
        : { phone };
      
      const response = await apiClient.post("/auth/forgot-password", payload);
      setCodeSent(true);
      if (response.data.demo_code) {
        setCode(response.data.demo_code);
        toast.success(t("demoCodeToast", { code: response.data.demo_code }));
      } else {
        toast.success(t("codeSentToast"));
      }
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      toast.error(t("enterSixDigitCode"));
      return;
    }

    setLoading(true);
    try {
      const payload = method === "email" 
        ? { email, code } 
        : { phone, code };
      
      const response = await apiClient.post("/auth/verify-reset-code", payload);
      
      localStorage.setItem("reset_token", response.data.reset_token);
      setStep("reset");
      toast.success(t("codeVerified"));
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      toast.error(t("passwordMinLength"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("passwordsMismatch"));
      return;
    }

    setLoading(true);
    try {
      const token = resetToken || localStorage.getItem("reset_token");
      
      await apiClient.post("/auth/reset-password", {
        token,
        new_password: newPassword
      });
      
      localStorage.removeItem("reset_token");
      toast.success(t("passwordResetSuccess"));
      navigate("/login");
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const cardDescription = step === "reset"
    ? t("forgotResetSubtitle")
    : codeSent
      ? t("forgotEnterCodeSubtitle")
      : t("forgotSubtitle");

  return (
    <FormPageShell centered={false} className="bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-md mx-auto w-full">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <img src={LOGO_URL} alt="OMU" className="h-12 w-auto" />
        </Link>
        <Card className="w-full max-w-md border-0 shadow-soft">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-omu-blue-100 rounded-full flex items-center justify-center mb-4">
              <KeyRound className="h-6 w-6 text-omu-blue-500" />
            </div>
            <CardTitle className="font-heading text-2xl">
              {step === "reset" ? t("newPassword") : t("forgotTitle")}
            </CardTitle>
            <CardDescription>
              {cardDescription}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-4">
            {step === "request" && !codeSent && (
              <>
                <Tabs value={method} onValueChange={setMethod} className="mb-6">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {t("email")}
                    </TabsTrigger>
                    <TabsTrigger value="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {t("phone")}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <form onSubmit={handleRequestReset} className="space-y-4">
                  {method === "email" ? (
                    <div className="space-y-2">
                      <Label htmlFor="email">{t("emailAddress")}</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("emailPlaceholder")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12 rounded-xl"
                        autoComplete="off"
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t("phoneNumber")}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder={t("phonePlaceholder")}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="h-12 rounded-xl"
                        autoComplete="off"
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      t("sendCode")
                    )}
                  </Button>
                </form>
              </>
            )}

            {step === "request" && codeSent && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="p-4 bg-green-50 rounded-xl flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {t("codeSent")}
                    </p>
                    <p className="text-sm text-green-600">
                      {method === "email" ? t("codeSentCheckEmail") : t("codeSentCheckPhone")}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="code">{t("enterCode")}</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="h-12 rounded-xl text-center text-2xl tracking-widest"
                    maxLength={6}
                    autoComplete="off"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                  disabled={loading || code.length !== 6}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    t("verifyCode")
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setCodeSent(false)}
                >
                  {t("resendCode")}
                </Button>
              </form>
            )}

            {step === "reset" && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t("newPassword")}</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-12 rounded-xl"
                    autoComplete="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t("confirmPassword")}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 rounded-xl"
                    autoComplete="new-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    t("resetPassword")
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-slate-500 hover:text-omu-blue-500 inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("backToLogin")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </FormPageShell>
  );
};

export default ForgotPasswordPage;
